import { Middleware } from "@floating-ui/dom";
import {
    arrow,
    autoUpdate,
    flip,
    type Placement,
    shift,
    type Strategy,
    useFloating,
    VirtualElement,
} from "@floating-ui/react-dom";
import { getZoomCorrectionLevel } from "@utils/zoom.utils";
import React, { CSSProperties, ReactNode, useEffect, useRef } from "react";

export type ForcedPopperPositions = {
    top?: number;
    left?: number;
    right?: number;
    bottom?: number;
};


interface FloatingPopperProps {
    referenceElement: VirtualElement | HTMLElement | null;
    placement?: Placement;
    strategy?: Strategy;
    modifiers?: Middleware[];
    useZoomCorrection?: boolean;
    children: (popperProps: FloatingPopperChildrenProps) => ReactNode;
    forcedPosition?: ForcedPopperPositions;
}

export interface FloatingPopperChildrenProps {
    ref: (node: HTMLElement) => void;
    style: CSSProperties;

    placement: Placement;
    isReferenceHidden?: boolean;
    hasPopperEscaped?: boolean;

    update: () => void;
    arrowProps: {
        ref: React.MutableRefObject<any>;
        style: React.CSSProperties;
    };
}

const FloatingPopper: React.FC<FloatingPopperProps> = ({
                                                           referenceElement,
                                                           placement = "bottom",
                                                           strategy = "absolute",
                                                           modifiers = [flip(), shift()],
                                                           useZoomCorrection = true,
                                                           children,
                                                           forcedPosition
                                                       }) => {
    const arrowRef = useRef(null);

    const zoom = useZoomCorrection ? getZoomCorrectionLevel() : 1;

    const zoomCorrectionMiddleware = {
        name: 'zoomCorrection',
        // @ts-ignore
        fn({ x, y }) {
            return {
                x: x / zoom,
                y: y / zoom,
            };
        },
    };


    const {
        x,
        y,
        strategy: computedStrategy,
        placement: computedPlacement,
        refs,
        update,
        middlewareData
    } = useFloating({
        placement,
        strategy,
        middleware: [
            ...modifiers,
            flip(),
            shift(),
            arrow({ element: arrowRef }),
            zoomCorrectionMiddleware,
        ],
    });

    const arrowStyle: CSSProperties = {
        left: middlewareData.arrow?.x != null ? `${middlewareData.arrow.x}px` : '',
        top: middlewareData.arrow?.y != null ? `${middlewareData.arrow.y}px` : '',
    };

    // Attach the external reference element
    useEffect(() => {
        if (referenceElement) {
            refs.setReference(referenceElement);
        }
    }, [referenceElement, refs]);

    // Auto-update positioning
    useEffect(() => {
        if (refs.reference.current && refs.floating.current) {
            return autoUpdate(refs.reference.current, refs.floating.current, update);
        }
        return null;
    }, [refs.reference, refs.floating, update]);

 
    
    const coordinates = forcedPosition ? {...forcedPosition} : {top: y ?? 0, left: x ?? 0};



    const popperProps: FloatingPopperChildrenProps = {
        ref: refs.setFloating,
        style: {
            position: computedStrategy,
            ...coordinates
        } as React.CSSProperties,
        placement: computedPlacement,
        update,
        arrowProps: {
            style: arrowStyle,
            ref: arrowRef
        }
    };

    return <>{children(popperProps)}</>;
};

export default FloatingPopper;
